import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardActionArea,
  CardContent,
  Grid,
  Paper,
  TextField,
  IconButton,
  Chip,
  Stack,
  Button,
  Tooltip,
} from '@mui/material';
import {
  Hub as HubIcon,
  Assignment as MyRequestsIcon,
  SupervisorAccount as ManageIcon,
  PeopleAlt as OnboardingBuddyIcon,
  BadgeOutlined as GuestPassIcon,
  HealthAndSafety as DiagnosticsIcon,
  Agriculture as HarvestIcon,
  Security as ClearanceIcon,
  AdminPanelSettings as PimSyncIcon,
  MonitorHeart as OperationsIcon,
  Search as SearchIcon,
  VpnKey as KeyIcon,
  BugReport as BugReportIcon,
  Lightbulb as LightbulbIcon,
  Send as SendIcon,
  RestartAlt as ResetIcon,
  ArrowBack as BackIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { useEnvironment, useFeature } from '../EnvironmentContext';
import { isFeatureEnabled, type FeatureKey } from '../featureFlags';
import ToolNotice from '../components/ToolNotice';
import { useAssistant } from '../AssistantContext';
import { ConversationThread } from '../components/AssistantConversation';
import { getPopularRequestTypes, type PopularRequestType } from '../api/requests';
import { iconMap } from '../components/tileIcons';
import { isAccessibilityAuditor } from '../accessibilityAudit';
import { layout } from '../ui-theme/layout';

interface ActionTile {
  label: string;
  path: string;
  icon: React.ReactNode;
  requireRole?: (roles: string[]) => boolean;
  /**
   * Hides the tile when the flag is off. This page keeps its own tile list rather than consuming
   * `toolCatalog`, so a module gated there is still linked from here unless it is also gated here.
   */
  requireFeature?: FeatureKey;
}

const quickActions: ActionTile[] = [
  { label: 'Request Central', path: '/requests', icon: <HubIcon sx={{ fontSize: 32 }} /> },
  { label: 'My Requests', path: '/my-requests', icon: <MyRequestsIcon sx={{ fontSize: 32 }} /> },
  {
    label: 'Manage Requests',
    path: '/manage-requests',
    icon: <ManageIcon sx={{ fontSize: 32 }} />,
    requireRole: (roles) => roles.some((r) => (r.startsWith('AskIAM.Requests.') && r.endsWith('.Manage')) || r === 'AskIAM.Admin'),
  },
];

const userTools: ActionTile[] = [
  {
    label: 'Onboarding Buddy',
    path: '/onboarding-buddy',
    icon: <OnboardingBuddyIcon sx={{ fontSize: 32 }} />,
    requireFeature: 'IdentityTools.OnboardingBuddy',
  },
  {
    label: 'Guest Pass',
    path: '/guestpass',
    icon: <GuestPassIcon sx={{ fontSize: 32 }} />,
    requireRole: (roles) => roles.some((r) => r === 'AskIAM.GuestPass.Read' || r === 'AskIAM.GuestPass.Write' || r === 'AskIAM.Admin'),
  },
];

const adminTools: ActionTile[] = [
  {
    label: 'FIC Diagnostics',
    path: '/admin/diagnostics',
    icon: <DiagnosticsIcon sx={{ fontSize: 32 }} />,
    requireRole: (roles) => roles.some((r) => r === 'AskIAM.Admin'),
  },
  {
    label: 'Harvest Management',
    path: '/harvest',
    icon: <HarvestIcon sx={{ fontSize: 32 }} />,
    requireRole: (roles) => roles.some((r) => r === 'AskIAM.Harvest.Manage' || r === 'AskIAM.Admin'),
    requireFeature: 'IdentityTools.Harvest',
  },
  {
    label: 'Clearance',
    path: '/clearance',
    icon: <ClearanceIcon sx={{ fontSize: 32 }} />,
    requireRole: (roles) => roles.some((r) => r === 'AskIAM.Clearance.Read' || r === 'AskIAM.Admin'),
    requireFeature: 'IdentityTools.Clearance',
  },
  {
    label: 'PIM Approver Sync',
    path: '/pim-sync',
    icon: <PimSyncIcon sx={{ fontSize: 32 }} />,
    requireRole: (roles) => roles.some((r) => r === 'AskIAM.PimSync.Manage' || r === 'AskIAM.Admin'),
    requireFeature: 'IdentityTools.PimSync.Management',
  },
  {
    label: 'Operations Center',
    path: '/admin/operations-center',
    icon: <OperationsIcon sx={{ fontSize: 32 }} />,
    requireRole: (roles) => roles.some((r) =>
      r === 'AskIAM.Admin' ||
      r === 'AskIAM.MsApprove.Admin' ||
      (r.startsWith('AskIAM.Requests.') && r.endsWith('.Manage'))),
    requireFeature: 'IdentityTools.MsApproveOperations',
  },
];

const TileGrid: React.FC<{ items: ActionTile[]; roles: string[] }> = ({ items, roles }) => {
  const navigate = useNavigate();
  const { flags } = useEnvironment();
  const auditReadOnly = isAccessibilityAuditor(roles);
  const visible = items.filter(
    (t) =>
      (auditReadOnly || !t.requireRole || t.requireRole(roles)) &&
      (!t.requireFeature || isFeatureEnabled(flags, t.requireFeature)),
  );
  if (visible.length === 0) return null;

  return (
    <Grid container spacing={2}>
      {visible.map((action) => (
        <Grid size={{ xs: 12, sm: 6, md: 3 }} key={action.path}>
          <Card variant="outlined">
            <CardActionArea onClick={() => navigate(action.path)} sx={{ p: 2, textAlign: 'center' }}>
              <CardContent>
                <Box sx={{ color: 'primary.main', mb: 1 }}>{action.icon}</Box>
                <Typography variant="body2" fontWeight={500}>
                  {action.label}
                </Typography>
              </CardContent>
            </CardActionArea>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
};

/** The classic tile-grid home, kept as the fallback when the assistant surface is turned off. */
const ClassicHome: React.FC<{ roles: string[] }> = ({ roles }) => {
  const { flags } = useEnvironment();
  const auditReadOnly = isAccessibilityAuditor(roles);
  // Mirrors TileGrid's filter. Without the flag check the Admin Tools heading would render above an
  // empty grid whenever every tile under it is gated off.
  const hasAdminTools = adminTools.some(
    (t) =>
      (auditReadOnly || !t.requireRole || t.requireRole(roles)) &&
      (!t.requireFeature || isFeatureEnabled(flags, t.requireFeature)),
  );

  return (
    <>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h2" gutterBottom>
          Welcome to AskIAM
        </Typography>
        <Typography color="text.secondary">
          Your central hub for identity and access support and assistance. Use the navigation
          on the left to access different services.
        </Typography>
      </Box>

      <Typography variant="h3" gutterBottom>
        Quick Actions
      </Typography>
      <TileGrid items={quickActions} roles={roles} />

      <Typography variant="h3" gutterBottom sx={{ mt: 4 }}>
        User Tools
      </Typography>
      <TileGrid items={userTools} roles={roles} />

      {hasAdminTools && (
        <>
          <Typography variant="h3" gutterBottom sx={{ mt: 4 }}>
            Admin Tools
          </Typography>
          <TileGrid items={adminTools} roles={roles} />
        </>
      )}
    </>
  );
};

/** One-click conversation starters, each seeding the shared assistant thread with a plain question. */
const STARTER_PROMPTS: { key: string; label: string; icon: React.ReactNode; prompt: string }[] = [
  { key: 'find-form', label: 'Find the right form', icon: <SearchIcon fontSize="small" />, prompt: 'Help me find the right AskIAM form for what I need to do.' },
  { key: 'request-access', label: 'Request access', icon: <KeyIcon fontSize="small" />, prompt: 'I need to request access to something in AskIAM. Where do I start?' },
  { key: 'report-bug', label: 'Report a bug', icon: <BugReportIcon fontSize="small" />, prompt: 'I want to report a bug in AskIAM.' },
  { key: 'share-idea', label: 'Share an idea', icon: <LightbulbIcon fontSize="small" />, prompt: 'I have an idea to improve AskIAM I would like to share.' },
];

/** Extracts a friendly first name from a display name in either "First Last" or "Last, First" form. */
function firstNameOf(displayName?: string): string {
  if (!displayName) return '';
  const afterComma = displayName.includes(',')
    ? displayName.split(',').slice(1).join(',').trim()
    : displayName.trim();
  return afterComma.split(/\s+/)[0] ?? '';
}

/** A pill-shaped "Message AskIAM" composer, shared by the hero and the in-conversation view. */
const AskBox: React.FC<{
  onSend: (text: string) => void;
  loading: boolean;
  autoFocus?: boolean;
  disabled?: boolean;
  placeholder?: string;
}> = ({ onSend, loading, autoFocus, disabled, placeholder }) => {
  const [input, setInput] = React.useState('');
  const submit = () => {
    const text = input.trim();
    if (!text || loading || disabled) return;
    setInput('');
    onSend(text);
  };
  return (
    <Paper
      elevation={3}
      sx={{
        display: 'flex',
        alignItems: 'flex-end',
        gap: 1,
        p: 1,
        pl: 2,
        borderRadius: 6,
        width: '100%',
        maxWidth: 720,
      }}
    >
      <TextField
        fullWidth
        multiline
        maxRows={6}
        variant="standard"
        placeholder={placeholder ?? 'Message AskIAM…'}
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            submit();
          }
        }}
        autoFocus={autoFocus}
        disabled={disabled}
        inputProps={{ maxLength: 4000, 'aria-label': 'Message AskIAM' }}
        InputProps={{ disableUnderline: true }}
        sx={{ py: 0.5 }}
      />
      <IconButton color="primary" onClick={submit} disabled={loading || disabled || !input.trim()} aria-label="Send message">
        <SendIcon />
      </IconButton>
    </Paper>
  );
};

/** The most-active request types, shown under the composer until a conversation begins. */
const PopularRequests: React.FC = () => {
  const navigate = useNavigate();
  const [popular, setPopular] = React.useState<PopularRequestType[]>([]);

  React.useEffect(() => {
    let cancelled = false;
    getPopularRequestTypes(6)
      .then((data) => {
        if (!cancelled) setPopular(data);
      })
      .catch(() => {
        // The row is a convenience, not load-bearing — a failure just leaves it hidden.
        if (!cancelled) setPopular([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (popular.length === 0) return null;

  return (
    <Box sx={{ width: '100%', maxWidth: 900, mt: 2 }}>
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5, textAlign: 'center' }}>
        Most active in AskIAM
      </Typography>
      <Grid container spacing={2}>
        {popular.map((t) => (
          <Grid size={{ xs: 12, sm: 6, md: 4 }} key={t.requestType}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardActionArea
                onClick={() => navigate(`/requests/new/${t.requestType}`)}
                sx={{ p: 2, height: '100%', display: 'flex', alignItems: 'flex-start' }}
              >
                <CardContent sx={{ p: 0 }}>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Box sx={{ color: 'primary.main', display: 'flex' }}>
                      {iconMap[t.icon] ?? iconMap.Description}
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600}>
                        {t.displayName}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      >
                        {t.description}
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

/** The Copilot-style home: a centered composer with starters, or the live conversation once started. */
const AssistantHome: React.FC<{ name: string }> = ({ name }) => {
  const {
    messages,
    loading,
    collectStatus,
    sendMessage,
    resetConversation,
    sendFeedback,
    startCollect,
    submitCollectAnswer,
    confirmCollectSubmit,
    cancelCollect,
  } = useAssistant();
  const collectEnabled = useFeature('Assistant.Collect');
  const navigate = useNavigate();
  // "Back" returns to the landing hero while keeping the thread intact; sending again resumes it.
  // "New conversation" wipes the thread. Kept separate so Back is a non-destructive escape hatch.
  const [collapsed, setCollapsed] = React.useState(false);
  const send = React.useCallback(
    (t: string) => {
      setCollapsed(false);
      if (collectStatus === 'collecting') {
        void submitCollectAnswer(t);
      } else {
        void sendMessage(t);
      }
    },
    [sendMessage, submitCollectAnswer, collectStatus],
  );
  const startNew = React.useCallback(() => {
    setCollapsed(false);
    resetConversation();
  }, [resetConversation]);
  const handleConfirmCollect = React.useCallback(() => {
    void confirmCollectSubmit().then((id) => {
      if (id) navigate(`/requests/${id}`);
    });
  }, [confirmCollectSubmit, navigate]);
  const hasConversation = messages.length > 0;

  // During a review/hand-off the composer is not the collection channel — the user acts via the card
  // buttons — so it is disabled with a prompt rather than silently feeding guidance chat.
  const awaitingCardAction = collectStatus === 'review' || collectStatus === 'handoff';
  // Any non-idle collection (including 'collecting') must keep the thread open: collapsing to the hero
  // would let a starter prompt or stray message become the answer to the hidden current field.
  const collectionActive = collectStatus !== 'idle';
  const composerPlaceholder =
    collectStatus === 'collecting'
      ? 'Type your answer…'
      : awaitingCardAction
        ? 'Use the buttons above to continue…'
        : undefined;

  if (hasConversation && (!collapsed || collectionActive)) {
    return (
      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <Box sx={{ width: '100%', maxWidth: 800, mx: 'auto', display: 'flex', justifyContent: 'space-between' }}>
          <Tooltip title="Back to home">
            <span>
              <Button
                size="small"
                startIcon={<BackIcon fontSize="small" />}
                onClick={() => setCollapsed(true)}
                disabled={loading || collectionActive}
              >
                Home
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Start a new conversation">
            <span>
              <Button size="small" startIcon={<ResetIcon fontSize="small" />} onClick={startNew} disabled={loading}>
                New conversation
              </Button>
            </span>
          </Tooltip>
        </Box>
        <Box sx={{ flexGrow: 1, overflowY: 'auto', minHeight: 0, width: '100%', maxWidth: 800, mx: 'auto', py: 2 }}>
          <ConversationThread
            messages={messages}
            loading={loading}
            onNavigate={() => {}}
            onFeedback={sendFeedback}
            onStartCollect={collectEnabled && collectStatus === 'idle' ? startCollect : undefined}
            onConfirmCollect={handleConfirmCollect}
            onCancelCollect={cancelCollect}
            collectionActive={collectionActive}
          />
        </Box>
        <Box sx={{ width: '100%', maxWidth: 800, mx: 'auto', display: 'flex', justifyContent: 'center', pt: 1 }}>
          <AskBox onSend={send} loading={loading} autoFocus disabled={awaitingCardAction} placeholder={composerPlaceholder} />
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 3,
        py: 4,
      }}
    >
      <Stack spacing={1} alignItems="center" textAlign="center">
        <Box
          component="img"
          src="/iamp-logo.png"
          alt="Identity & Access Management Platform"
          sx={{ width: 220, height: 220, mb: 0.5 }}
        />
        <Typography variant="h3" fontWeight={600}>
          {name ? `How can I help, ${name}?` : 'How can I help?'}
        </Typography>
        <Typography color="text.secondary">
          Ask a question or describe a problem, and I'll point you to the right path in AskIAM.
        </Typography>
      </Stack>

      <AskBox onSend={send} loading={loading} autoFocus />

      <Stack direction="row" spacing={1} flexWrap="wrap" justifyContent="center" useFlexGap>
        {STARTER_PROMPTS.map((s) => (
          <Chip
            key={s.key}
            icon={s.icon as React.ReactElement}
            label={s.label}
            variant="outlined"
            onClick={() => send(s.prompt)}
            disabled={loading}
            sx={{ cursor: 'pointer' }}
          />
        ))}
      </Stack>

      <PopularRequests />
    </Box>
  );
};

const HomePage: React.FC = () => {
  const { user } = useAuth();
  const { baseUrl } = useEnvironment();
  const assistantHomeEnabled = useFeature('Assistant.HomeExperience');
  const assistantGuidanceEnabled = useFeature('Assistant.Guidance');
  const assistantEnabled = assistantHomeEnabled && assistantGuidanceEnabled;
  const userRoles = user?.roles ?? [];

  const isWrongUrl = baseUrl
    ? !window.location.origin.toLowerCase().startsWith(baseUrl.toLowerCase().replace(/\/$/, ''))
    : false;

  return (
    <Box
      sx={{
        p: 4,
        ...(assistantEnabled
          ? { display: 'flex', flexDirection: 'column', minHeight: `calc(100vh - ${layout.headerHeight}px)` }
          : {}),
      }}
    >
      {isWrongUrl && (
        <ToolNotice
          toolKey="url-redirect"
          title="Bookmark Update"
          severity="info"
          message={`You're accessing AskIAM through a non-standard URL. Please update your bookmark to ${baseUrl} for the best experience.`}
        />
      )}

      {assistantEnabled ? (
        <AssistantHome name={firstNameOf(user?.name)} />
      ) : (
        <ClassicHome roles={userRoles} />
      )}
    </Box>
  );
};

export default HomePage;
